/* ------------------------------------------------------------------
 * <nr-marquee> — responsive infinite news ticker for the announcement bar.
 *
 * The bar renders one content "group" (plus a fallback copy). This element
 * measures that group and the container, then repeats the group enough times
 * to always fill the screen — so it looks full and seamless on phones,
 * tablets and wide laptops alike. It re-runs on resize. The CSS animation
 * shifts the track by exactly ONE group width, which keeps the loop seamless
 * and the scroll speed constant at every screen size.
 * ------------------------------------------------------------------ */
if (!customElements.get("nr-marquee")) {
  customElements.define(
    "nr-marquee",
    class NrMarquee extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector(".nr-marquee-track");
        if (!this.track) return;

        const firstGroup = this.track.querySelector(".nr-marquee-group");
        if (!firstGroup) return;

        // Remember the original single-group markup as the repeating unit.
        this.unitHTML = firstGroup.innerHTML;

        this.build();

        this._onResize = this.debounce(() => this.build(), 150);
        window.addEventListener("resize", this._onResize);

        // Rebuild once web fonts settle (their width can change the measurement).
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.build());
        }
      }

      disconnectedCallback() {
        if (this._onResize) window.removeEventListener("resize", this._onResize);
      }

      makeGroup(hidden) {
        const g = document.createElement("div");
        g.className = "nr-marquee-group";
        if (hidden) g.setAttribute("aria-hidden", "true");
        g.innerHTML = this.unitHTML;
        return g;
      }

      build() {
        const track = this.track;

        // Measure one group in isolation.
        track.style.setProperty("--nr-marquee-shift", "0px");
        track.innerHTML = "";
        const first = this.makeGroup(false);
        track.appendChild(first);

        const unitWidth = first.getBoundingClientRect().width;
        const containerWidth = this.getBoundingClientRect().width;
        if (!unitWidth || !containerWidth) return;

        // Enough copies so the track always overflows the container by at least
        // one full group (guarantees no empty gap as it scrolls).
        let copies = Math.ceil((containerWidth + unitWidth) / unitWidth) + 1;
        if (copies < 2) copies = 2;

        for (let i = 1; i < copies; i++) {
          track.appendChild(this.makeGroup(true));
        }

        // Shift by exactly one group width -> seamless loop, constant speed.
        track.style.setProperty("--nr-marquee-shift", "-" + unitWidth + "px");
      }

      debounce(fn, wait) {
        let t;
        return () => {
          clearTimeout(t);
          t = setTimeout(fn, wait);
        };
      }
    }
  );
}
